/**
 * CertificationTrackerPage — `/franchise/certification`.
 *
 * Tracks training-certificate status across the franchise network. Backed by
 * the dedicated `/v1/franchise/certifications` surface (owns kind, expiry,
 * revoke audit). The legacy lifecycle filter is kept as a secondary filter
 * because the franchise list is still useful when issuing a new cert.
 */
import { type JSX, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../lib/api/client';
import {
  type CertStatus,
  type FranchiseCertification,
  issueCertification,
  listCertifications,
  listFranchises,
  revokeCertification,
} from '../../lib/api/franchise';
import { DeferredNotice, PageHeader, StatusBadge } from '../shared/StaffUI';
import { FilterBar } from '../mail/shared/MailUI';

const ALL_STATUSES: readonly CertStatus[] = ['ISSUED', 'EXPIRED', 'REVOKED'];

export function CertificationTrackerPage(): JSX.Element {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<CertStatus | ''>('');
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [issueOpen, setIssueOpen] = useState(false);
  const [issueFranchiseId, setIssueFranchiseId] = useState('');
  const [issueRef, setIssueRef] = useState('');
  const [issueKind, setIssueKind] = useState('NIPOST-OPS');
  const [issueExpires, setIssueExpires] = useState('');

  const list = useQuery({
    queryKey: ['franchise', 'certifications', statusFilter],
    queryFn: () =>
      listCertifications(statusFilter === '' ? {} : { status: statusFilter }),
    retry: false,
  });

  const franchises = useQuery({
    queryKey: ['franchise', 'certifications', 'franchises'],
    queryFn: () => listFranchises({ pageSize: 100 }),
    retry: false,
  });

  const denied = list.isError && list.error instanceof ApiError &&
    (list.error.status === 401 || list.error.status === 403);

  const items: readonly FranchiseCertification[] = useMemo(() => {
    const raw = list.data;
    const all: readonly FranchiseCertification[] = Array.isArray(raw) ? raw : [];
    if (search.trim().length === 0) return all;
    const q = search.toLowerCase();
    return all.filter(
      (c) =>
        c.certificateRef.toLowerCase().includes(q) ||
        c.certKind.toLowerCase().includes(q) ||
        c.franchiseId.toLowerCase().includes(q),
    );
  }, [list.data, search]);

  const issue = useMutation({
    mutationFn: () =>
      issueCertification({
        franchiseId: issueFranchiseId,
        certKind: issueKind,
        certificateRef: issueRef,
        expiresAt: issueExpires.length > 0 ? new Date(issueExpires).toISOString() : undefined,
      }),
    onSuccess: () => {
      setIssueOpen(false);
      setIssueRef('');
      setIssueExpires('');
      void qc.invalidateQueries({ queryKey: ['franchise', 'certifications'] });
    },
    onError: (e: Error) => setActionErr(e.message),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => {
      const reason = window.prompt('Revoke reason?') ?? '';
      if (reason.length === 0) throw new Error('Cancelled');
      return revokeCertification(id, reason);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['franchise', 'certifications'] });
    },
    onError: (e: Error) => setActionErr(e.message),
  });

  const franchiseIndex = useMemo(() => {
    const map = new Map<string, string>();
    for (const f of franchises.data?.items ?? []) {
      map.set(f.id, `${f.legalName} (${f.franchiseNumber})`);
    }
    return map;
  }, [franchises.data]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Certification tracker"
        description="Training certificate status across the franchise network."
        actions={
          <button
            type="button"
            onClick={() => setIssueOpen((v) => !v)}
            className="px-3 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark"
          >
            {issueOpen ? 'Close' : 'Issue certificate'}
          </button>
        }
      />

      {issueOpen ? (
        <section
          data-testid="cert-issue-form"
          className="bg-white border border-border rounded-xl p-5 space-y-3"
        >
          <h2 className="text-sm font-semibold text-gray-900">Issue new certification</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-muted mb-1">
                Franchise
              </label>
              <select
                value={issueFranchiseId}
                onChange={(e) => setIssueFranchiseId(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm"
              >
                <option value="">Select…</option>
                {franchises.data?.items.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.legalName} ({f.franchiseNumber})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-muted mb-1">
                Cert kind
              </label>
              <input
                type="text"
                value={issueKind}
                onChange={(e) => setIssueKind(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm font-mono"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-muted mb-1">
                Certificate reference
              </label>
              <input
                type="text"
                value={issueRef}
                onChange={(e) => setIssueRef(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm font-mono"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-muted mb-1">
                Expires (optional)
              </label>
              <input
                type="date"
                value={issueExpires}
                onChange={(e) => setIssueExpires(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm"
              />
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              setActionErr(null);
              issue.mutate();
            }}
            disabled={
              issueFranchiseId.length === 0 ||
              issueRef.trim().length === 0 ||
              issue.isPending
            }
            className="px-3 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark disabled:opacity-50"
          >
            {issue.isPending ? 'Issuing…' : 'Issue'}
          </button>
        </section>
      ) : null}

      <FilterBar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Filter by ref, kind or franchise id…"
      >
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as CertStatus | '')}
          className="px-3 py-2 border border-border rounded-lg text-sm"
        >
          <option value="">All cert states</option>
          {ALL_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </FilterBar>

      {list.isPending ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : denied ? (
        <DeferredNotice title="Permission required">
          <code className="font-mono">FranchiseCertification/read</code> required.
        </DeferredNotice>
      ) : list.isError ? (
        <DeferredNotice title="Service unreachable">{(list.error as Error).message}</DeferredNotice>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted bg-white border border-border rounded-xl p-6 text-center">
          No certifications match the filter.
        </p>
      ) : (
        <div className="bg-white border border-border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
              <tr>
                <th className="text-left px-4 py-2">Franchise</th>
                <th className="text-left px-4 py-2">Kind</th>
                <th className="text-left px-4 py-2">Ref</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2">Issued</th>
                <th className="text-left px-4 py-2">Expires</th>
                <th className="text-right px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {items.map((c) => (
                <tr key={c.id} className="hover:bg-gray-50" data-testid="cert-row">
                  <td className="px-4 py-2 text-xs text-gray-700">
                    {franchiseIndex.get(c.franchiseId) ?? c.franchiseId}
                  </td>
                  <td className="px-4 py-2 text-xs font-mono">{c.certKind}</td>
                  <td className="px-4 py-2 font-mono text-xs">{c.certificateRef}</td>
                  <td className="px-4 py-2"><StatusBadge status={c.status} /></td>
                  <td className="px-4 py-2 text-xs text-muted">
                    {new Date(c.issuedAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-2 text-xs text-muted">
                    {c.expiresAt ? new Date(c.expiresAt).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {c.status === 'ISSUED' ? (
                      <button
                        type="button"
                        onClick={() => {
                          setActionErr(null);
                          revoke.mutate(c.id);
                        }}
                        disabled={revoke.isPending}
                        className="text-xs font-semibold text-danger hover:underline disabled:opacity-50"
                      >
                        Revoke
                      </button>
                    ) : (
                      <span className="text-xs text-muted">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {actionErr ? <p className="text-sm text-danger">{actionErr}</p> : null}
    </div>
  );
}
